import { CanActivate, CanActivateChild, CanMatch, GuardResult, MaybeAsync, Router, UrlTree } from '@angular/router';
import { inject, Injectable } from '@angular/core';

import { APP_CONFIG } from '@kros-sk/core/app-config';
import { KrosAppInsightsService } from '@kros-sk/core/application-insights';

import { KrosAuthService } from '../services/auth/kros-auth.service';

const MAX_ALLOWED_TIMESHIFT_IN_SECONDS = 60;

@Injectable()
export class KrosAuthGuardService implements CanActivate, CanActivateChild, CanMatch {
  #appConfig = inject(APP_CONFIG);
  #authService = inject(KrosAuthService);
  #router = inject(Router);
  #logger = inject(KrosAppInsightsService);

  #isTimeChecked = false;

  canActivate(): MaybeAsync<GuardResult> {
    return this.checkAuthentication();
  }

  canActivateChild(): MaybeAsync<GuardResult> {
    return this.checkAuthentication();
  }

  canMatch(): MaybeAsync<GuardResult> {
    return this.checkAuthentication();
  }

  private async checkAuthentication(): Promise<boolean | UrlTree> {
    // client time verification
    // ToDo: nájsť request, ktorý by toto nastavoval
    const timeShift = this.getShiftTimeInSeconds();
    if (Math.abs(timeShift) > MAX_ALLOWED_TIMESHIFT_IN_SECONDS) return this.timeOutOfSyncRedirect(timeShift);

    // token validity verification
    const tokenIsValid = await this.#authService.ensureValidToken();
    if (tokenIsValid) {
      return true;
    }
    this.#authService.startAuthentication();
    return false;
  }

  private getShiftTimeInSeconds(): number {
    if (!this.#appConfig.verifyClientTime || this.#isTimeChecked) return 0;
    let timeShift = 0;

    if (
      this.#appConfig.telemetryInfo.serverResponseTime instanceof Date &&
      this.#appConfig.telemetryInfo.clientResponseProcessingTime instanceof Date
    ) {
      timeShift =
        (this.#appConfig.telemetryInfo.clientResponseProcessingTime.getTime() -
          this.#appConfig.telemetryInfo.serverResponseTime.getTime()) /
        1000;
    }

    this.#isTimeChecked = true;
    return timeShift;
  }

  private timeOutOfSyncRedirect(shift: number): UrlTree {
    this.#logger?.trackException(new Error('Time not synchronized'), 'Time shift', { shift });
    return this.#router.createUrlTree(['/time-not-synchronized'], { queryParams: { shift } });
  }
}
